'use client';

import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { EllipsisProgress, MenuItem, SvgIconWrapper, TextField, Typography } from 'lighthouse';
import { SearchOutlineMdV2SVG } from 'lighthouse-icons';

import { SelectCityModalProps } from '@/widgets/AppBar/_components/SelectCityModal/models';
import SwipeableDrawer from '@/widgets/SwipeableDrawer';

import { useSelectCity } from '@/hooks/user/useSelectCity';

import { getAgentLocation } from '@/utilities/getAgentLocation';
import { isPushNotificationSupported, requestClientPushNotification } from '@/utilities/requestClientPushNotification';

import { getCitiesService } from '@/client/services/geo/cities/methods';
import { CityItem, GetCitiesQueryParam } from '@/client/services/geo/cities/models';
import { cornbar } from 'cornbar';
import { debounce } from 'lodash-es';

export const SelectCityModal = ({ onClose, requestLocationOnOpen = true, allowClose = false }: SelectCityModalProps) => {
    const { handleSelectCity, isUserSelectedCity } = useSelectCity();

    const [search, setSearch] = useState('');
    const [isSearchLoading, setIsSearchLoading] = useState(true);
    const [cities, setCities] = useState<CityItem[]>([]);

    const handleGetCities = useCallback(
        async (search: string) => {
            setIsSearchLoading(true);
            try {
                if (search) {
                    const response = await getCitiesService({ search, isAvailableForMarketplace: true });
                    setCities(response.data.content);
                    return;
                }
                let location;
                if (requestLocationOnOpen) {
                    try {
                        location = await getAgentLocation();
                    } catch (err) {
                        console.error(err);
                    }
                }
                const params: GetCitiesQueryParam = location
                    ? {
                          lat: location.lat,
                          lng: location.lng,
                          sortOption: 'CENTER_DISTANCE_ASC',
                          size: 30
                      }
                    : {
                          size: 30,
                          sort: ['priority,asc']
                      };

                const response = await getCitiesService({ ...params, isAvailableForMarketplace: true });
                setCities(response.data.content);
            } catch {
                cornbar.show({
                    title: 'خطا در دریافت شهر‌ها',
                    description: 'لطفا دوباره تلاش کنید',
                    variant: 'error'
                });
            } finally {
                setIsSearchLoading(false);
            }
        },
        [setCities, requestLocationOnOpen]
    );

    useEffect(() => {
        handleGetCities('');
    }, []);

    const handleCityChange = (city: CityItem) => {
        if (isPushNotificationSupported() && Notification.permission === 'default') {
            void requestClientPushNotification();
        }
        handleSelectCity(city);
        onClose();
    };

    const handleDebouncedSearch = useMemo(() => debounce(handleGetCities, 500), [handleGetCities]);

    const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value.trim());
        handleDebouncedSearch(event.target.value.trim());
    };

    return (
        <SwipeableDrawer
            title='لطفا شهر خود را انتخاب کنید'
            open
            hasCloseButton={allowClose || isUserSelectedCity}
            onClose={allowClose || isUserSelectedCity ? onClose : undefined}>
            <div className='mt-3 px-4'>
                <TextField
                    InputProps={{ endAdornment: <SvgIconWrapper icon={SearchOutlineMdV2SVG} size='md' color='blackMediumEmphasis' /> }}
                    value={search}
                    label='جست‌وجو کنید...'
                    onChange={handleSearchChange}
                />
            </div>
            <div className='max-h-96 min-h-44 overflow-hidden overflow-y-auto px-4'>
                <ul className='m-0 list-none px-0 py-4'>
                    {isSearchLoading ? (
                        <li className='flex h-44 flex-col items-center justify-center'>
                            <EllipsisProgress color='primary' />
                            <Typography variant='caption' className='mt-5' color='blackMediumEmphasis'>
                                درحال جست‌وجو لطفا منتظر بمانید
                            </Typography>
                        </li>
                    ) : cities.length ? (
                        cities.map((city) => (
                            <MenuItem divider key={city.id} onClick={() => handleCityChange(city)}>
                                <Typography variant='body1' component='div'>
                                    {city.name}
                                </Typography>
                            </MenuItem>
                        ))
                    ) : (
                        <Typography
                            className='flex h-44 items-center justify-center'
                            variant='caption'
                            color='blackMediumEmphasis'
                            align='center'
                            component='li'>
                            شهری مطابق با جستوجوی شما یافت نشد...
                        </Typography>
                    )}
                </ul>
            </div>
        </SwipeableDrawer>
    );
};
